import React, { useEffect, useState } from "react";
import CardImage from "../../../../assets/image/icons/CreditCard.png";
import AxiosInstance from "../../../AxiosInstance";
import sendToast from "../../../../components/Toast/sendToast";
import AddCard from "../../Account/AddCard";
import { Grid, CircularProgress } from "@mui/material";
import AddCardDetailsForm from "../../../../components/AdminViews/AddCardDetailsForm";
import {
  getCardType,
  getTokenizationKeyCustomer,
} from "../../../../plugins/ApiHandler";
import { IconButton } from "@mui/material";
import Delete from "../../../../assets/image/icons/delete.svg";
import "./style.css";
import sendSwal from "../../../../components/Swal/sendSwal";
import { LoaderComponent } from "../../../../components/Icon/Index";
import showToast from "../../../../components/Toast/Toster";
import { Typography } from "@mui/material";
import BlueButton from "../../../../components/Button/BlueButton";
import { useUserType } from "../../../../contexts/UserTypeContext";
import { getCardImageFromNumber, getFreeCardImageAsync, getAlternativeCardImages } from "../../../../utils/cardImageHelper";
import { getItem } from "../../../../utils/storage";

// Default card icon: shows CreditCard.png when available, otherwise inline SVG (avoids broken image when PNG is missing)
const DefaultCardIcon = () => {
  const [imgFailed, setImgFailed] = useState(false);
  if (imgFailed) {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "90%", height: "90%", objectFit: "contain" }}>
        <path d="M20 4H4C2.89 4 2.01 4.89 2.01 6L2 18C2 19.11 2.89 20 4 20H20C21.11 20 22 19.11 22 18V6C22 4.89 21.11 4 20 4ZM20 18H4V12H20V18ZM20 8H4V6H20V8Z" fill="#0f2b46" fillOpacity="0.7" />
      </svg>
    );
  }
  return (
    <img
      src={CardImage}
      alt=""
      width="90%"
      height="90%"
      style={{ objectFit: "contain" }}
      onError={() => setImgFailed(true)}
    />
  );
};

// Component to handle image loading with fallbacks - ensures ALL cards show images
const CardImageWithFallback = ({ primaryImage, alternativeImages = [], defaultImage, alt }) => {
  const [imageSrc, setImageSrc] = useState(primaryImage || defaultImage);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Reset when primary image changes
    setImageSrc(primaryImage || defaultImage);
    setCurrentIndex(0);
    setHasError(false);
  }, [primaryImage, defaultImage]);

  const handleImageError = (e) => {
    if (hasError) return; // Prevent infinite loops
    
    // Try alternative images in order
    if (currentIndex < alternativeImages.length) {
      const nextImage = alternativeImages[currentIndex];
      if (nextImage) {
        setImageSrc(nextImage);
        setCurrentIndex(currentIndex + 1);
        return;
      }
    }
    
    // If all alternatives failed, use default
    if (e.target.src !== defaultImage) {
      setImageSrc(defaultImage);
      setHasError(true);
    }
  };

  return (
    <img
      src={imageSrc}
      alt={alt}
      width="90%"
      height="90%"
      onError={handleImageError}
      style={{ objectFit: "contain" }}
      loading="lazy"
    />
  );
};

const CardPayment = ({
  paymentFormik,
  selectedCard,
  setSelectedCard,
  CustomerId,
  CompanyId,
  tokenDecode,
  disableDejavoo = false,
  onBeforeDeleteConfirm,
}) => {
  const [billingDetails, setBillingDetails] = useState([]);

  const [loader, setloader] = useState(true);
  const [customerVault, setCustomerVault] = useState("");
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  const [dejavooLoading, setDejavooLoading] = useState(false);
  const isDejavooGateway = !disableDejavoo && (tokenDecode?.Gateway || "").toUpperCase() === "DEJAVOO";
  
  // Debug logs
  console.log("CardPayment - tokenDecode:", tokenDecode);
  console.log("CardPayment - tokenDecode?.Gateway:", tokenDecode?.Gateway);
  console.log("CardPayment - disableDejavoo:", disableDejavoo);
  console.log("CardPayment - isDejavooGateway:", isDejavooGateway);
  const gatewayToPass = disableDejavoo ? undefined : (tokenDecode?.Gateway || "");
  console.log("CardPayment - gatewayToPass:", gatewayToPass);

  useEffect(() => {
    if (billingDetails?.length === 1 && !selectedCard) {
      const singleCard = billingDetails[0];
      setSelectedCard(singleCard);
      paymentFormik.setFieldValue("billing_id", singleCard["@attributes"]?.id ?? singleCard.billing_id);
      paymentFormik.setFieldValue("customer_vault_id", customerVault);
      if (singleCard?.token != null) {
        paymentFormik.setFieldValue("token", singleCard.token);
      }
    }
  }, [billingDetails, customerVault]);

  const getCardImage = async (cardNumberOrType, cardType = null) => {
    try {
      // Use free card image API - no rate limits, supports all card types worldwide
      if (!cardNumberOrType && !cardType) return CardImage;
      
      let imageUrl = null;
      
      // ALWAYS try to detect from card number first (most reliable)
      if (cardNumberOrType && /\d/.test(cardNumberOrType)) {
        // It's a card number (even if masked like "4XXXXXXXXXXX4242")
        imageUrl = await getCardImageFromNumber(cardNumberOrType);
        if (imageUrl) return imageUrl;
        
        // Also try with card type if available
        if (cardType) {
          imageUrl = await getFreeCardImageAsync(cardType, cardNumberOrType);
          if (imageUrl) return imageUrl;
        }
      }
      
      // Try using cardType parameter
      if (cardType) {
        imageUrl = await getFreeCardImageAsync(cardType, cardNumberOrType);
        if (imageUrl) return imageUrl;
      }
      
      // Try using cardNumberOrType as card type if it's not a number
      if (cardNumberOrType && !/\d/.test(cardNumberOrType)) {
        imageUrl = await getFreeCardImageAsync(cardNumberOrType);
        if (imageUrl) return imageUrl;
      }
      
      // Final fallback - try to detect from any digits in cardNumberOrType
      if (cardNumberOrType) {
        const digits = cardNumberOrType.replace(/\D/g, "");
        if (digits.length >= 1) {
          imageUrl = await getCardImageFromNumber(digits);
          if (imageUrl) return imageUrl;
        }
      }
      
      return CardImage;
    } catch (error) {
      console.error("Error getting card image:", error?.message);
      return CardImage;
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const [modalShow, setModalShow] = useState(false);

  const fetchCardData = async () => {
    if (!CustomerId) {
      sendToast("We couldn't load saved cards. Please select a customer and try again.");
      return;
    }
    try {
      const storedCompanyId =
        getItem("CompanyId") || tokenDecode?.CompanyId;
      const finalCompanyId = CompanyId || storedCompanyId;

      const res = await AxiosInstance.post(
        "/v1/nmi/get-billing-customer-vault",
        {
          CompanyId: finalCompanyId,
          type: "card",
          CustomerId,
          Gateway: tokenDecode?.Gateway,
        }
      );
      if (
        res?.data?.status === 200 &&
        res?.data?.data &&
        res?.data?.data?.customer &&
        res?.data?.data?.customer?.billing
      ) {
        const billingData = [];
        setCustomerVault(res?.data?.data?.customer["@attributes"]?.id);

        paymentFormik.setFieldValue(
          "customer_vault_id",
          res?.data?.data?.customer["@attributes"]?.id
        );
        if (Array.isArray(res?.data?.data?.customer?.billing)) {
          for (const elem of res?.data?.data?.customer?.billing) {
            // Get card number and type from billing data - try multiple possible fields
            const cardNumber = elem?.cc_number || 
                             elem?.["@attributes"]?.cc_number || 
                             elem?.ccnumber ||
                             elem?.["@attributes"]?.ccnumber ||
                             "";
            const cardTypeFromApi = elem?.cc_type || 
                                  elem?.["@attributes"]?.cc_type ||
                                  elem?.cardType ||
                                  "";
            
            // Get card image using card number (even if masked, first digit works) and card type
            const image = await getCardImage(cardNumber, cardTypeFromApi);
            
            // Get alternative images for fallback - ALWAYS get alternatives
            const alternativeImages = getAlternativeCardImages(cardTypeFromApi, cardNumber) || [];
            
            const cardType = await getCardType(
              elem?.cc_bin || elem?.["@attributes"]?.cc_bin,
              cardTypeFromApi
            );
            billingData.push({
              ...elem,
              image: image || "",
              alternativeImages: alternativeImages || [],
              cardType:
                cardType ||
                cardTypeFromApi ||
                "",
            });
          }
        } else {
          const billing = res?.data?.data?.customer?.billing;
          // Get card number and type from billing data - try multiple possible fields
          const cardNumber = billing?.cc_number || 
                           billing?.["@attributes"]?.cc_number || 
                           billing?.ccnumber ||
                           billing?.["@attributes"]?.ccnumber ||
                           "";
          const cardTypeFromApi = billing?.cc_type || 
                                billing?.["@attributes"]?.cc_type ||
                                billing?.cardType ||
                                "";
          
          // Get card image using card number and card type
          const image = await getCardImage(cardNumber, cardTypeFromApi);
          
          // Get alternative images for fallback - ALWAYS get alternatives
          const alternativeImages = getAlternativeCardImages(cardTypeFromApi, cardNumber) || [];
          
          const cardTypeDetected = await getCardType(
            billing?.cc_bin || billing?.["@attributes"]?.cc_bin,
            cardTypeFromApi
          );
          billingData.push({
            ...billing,
            image: image || "",
            alternativeImages: alternativeImages || [],
            cardType:
              cardTypeDetected ||
              cardTypeFromApi ||
              "",
          });
        }
        setBillingDetails(billingData);
      } else {
        setBillingDetails([]);
      }
    } catch (error) {
      sendToast("We couldn't load saved cards. Please try again in a moment.");
    } finally {
      setloader(false);
    }
  };

  useEffect(() => {
    if (!CustomerId) return;
    setloader(true);
    fetchCardData();
  }, [CompanyId, CustomerId, tokenDecode?.Gateway]);

  const handleDelete = async (item) => {
    onBeforeDeleteConfirm?.();
    sendSwal(isDonationMode).then(async (willDelete) => {
      if (willDelete) {
        try {
          if (item["@attributes"]?.id) {
            const storedCompanyId = getItem("CompanyId");
            const finalCompanyId = CompanyId || storedCompanyId;

            const object = {
              CompanyId: finalCompanyId,
              customer_vault_id: customerVault,
              billing_id: item["@attributes"]?.id,
            };
            if (billingDetails?.length > 1) {
              var url = `/v1/nmi/delete-customer-billing`;
            } else {
              url = `/v1/nmi/delete-customer-vault`;
            }

            const deleteRes = await AxiosInstance.post(url, object);
            if (deleteRes?.data?.status === 200) {
              showToast.success("Card deleted successfully");
              fetchCardData();
            } else {
              const errMsg =
                deleteRes?.data?.data?.error ||
                deleteRes?.data?.error ||
                "Failed to delete card.";
              showToast.error(errMsg);
            }
          }
        } catch (error) {
          const errMsg =
            error?.response?.data?.data?.error ||
            error?.response?.data?.error ||
            error?.message ||
            "We couldn't delete this card. Please try again in a moment.";
          showToast.error(errMsg);
        }
      }
    });
  };

  const [isPublicKeyAvailable, setIsPublicKeyAvailable] = useState(false);
  const getNmiKey = async () => {
    try {
      const keyResponse = await getTokenizationKeyCustomer(CustomerId);
      if (keyResponse?.PublicKey) {
        setIsPublicKeyAvailable(true);
      } else {
        setIsPublicKeyAvailable(false);
      }
    } catch (error) {
      console.error(error?.message);
    }
  };
  useEffect(() => {
    getNmiKey();
  }, []);

  return (
    <Grid
      className="mb-3 px-2 d-flex flex-column accountInformationInputTop creditCardBoxSelectDate customer-card-payment-list"
      sx={{
        background: "transparent",
        borderRadius: "8px",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "visible",
        overflowY: "visible",
        boxSizing: "border-box",
      }}
    >
      {loader ? (
        <Grid className="d-flex flex-direction-row justify-content-center align-items-center p-5 m-5">
          <LoaderComponent variant="skeleton"
            loader={loader}
            isDonationMode={isDonationMode}
            height="50"
            width="50"
          />
        </Grid>
      ) : billingDetails && billingDetails?.length > 0 ? (
        <div className="payment-cards-container">
          {billingDetails?.map((item, index) => {
            const isSelected = selectedCard === item;
            const cardExp = item?.cc_exp || item?.["@attributes"]?.cc_exp || "";
            const formattedExp = cardExp.length === 4 
              ? `${cardExp.slice(0, 2)}/${cardExp.slice(2, 4)}`
              : cardExp || "N/A";
            
            return (
              <div 
                key={index}
                className={`payment-card-row ${isSelected ? 'selected' : ''}`}
                onClick={(e) => {
                  if (!e.target.closest('.delete-card-btn')) {
                    setSelectedCard(item);
                    paymentFormik.setFieldValue(
                      "billing_id",
                      item["@attributes"]?.id ?? item.billing_id
                    );
                    paymentFormik.setFieldValue("token", item?.token ?? "");
                  }
                }}
              >
                <div className="card-radio-container">
                  <input
                    type="radio"
                    className="payment-card-radio"
                    checked={isSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCard(item);
                        paymentFormik.setFieldValue(
                          "billing_id",
                          item["@attributes"]?.id ?? item.billing_id
                        );
                        paymentFormik.setFieldValue("token", item?.token ?? "");
                      } else {
                        setSelectedCard("");
                        paymentFormik.setFieldValue("token", "");
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCard(item);
                      paymentFormik.setFieldValue(
                        "billing_id",
                        item["@attributes"]?.id ?? item.billing_id
                      );
                      paymentFormik.setFieldValue("token", item?.token ?? "");
                    }}
                  />
                </div>
                <div className="card-logo-container">
                  <CardImageWithFallback
                    primaryImage={item?.image}
                    alternativeImages={item?.alternativeImages || []}
                    defaultImage={CardImage}
                    alt="Card"
                  />
                </div>
                <div className="card-details-container">
                  <div className="card-number-text">
                    {item?.cc_number || "****"}
                  </div>
                  <div className="card-expiry-text">
                    {(item?.gateway || "").toString().toUpperCase() === "DEJAVOO"
                      ? "Credit"
                      : formattedExp && formattedExp !== "N/A"
                        ? `Credit - Valid Thru : ${formattedExp}`
                        : "Credit"}
                  </div>
                </div>
                <div className="card-delete-container">
                  <div 
                    className="delete-card-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item);
                    }}
                  >
                    <img src={Delete} alt="Delete" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="payment-cards-container payment-card-no-card">
          <div className="payment-card-no-card-row">
            <div className="payment-card-no-card-left">
              <div className="payment-card-no-card-icon">
                <DefaultCardIcon />
              </div>
              <Typography className="payment-card-no-card-text">
                No Credit/Debit Card
              </Typography>
            </div>
            {(isPublicKeyAvailable || isDejavooGateway || disableDejavoo) && (
              <BlueButton
                className="add-card-btn-light"
                isDonationMode={isDonationMode}
                onClick={() => setModalShow(true)}
                label={
                  <span className="add-card-btn-label add-card-btn-single-line">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                      <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Add Card
                  </span>
                }
              />
            )}
          </div>
        </div>
      )}

      <AddCard
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isBilling={billingDetails?.length > 0}
        vaultId={paymentFormik?.values?.customer_vault_id}
        fetchData={fetchCardData}
        CustomerId={CustomerId}
      />
      <AddCardDetailsForm
        onHide={() => setModalShow(false)}
        show={modalShow}
        CustomerId={CustomerId}
        fetchData={fetchCardData}
        CompanyId={CompanyId}
        Gateway={gatewayToPass}
      />
      {billingDetails && billingDetails?.length > 0 && (isPublicKeyAvailable || isDejavooGateway || disableDejavoo) && (
        <Grid className="d-flex justify-content-end mb-3 mx-2 AddCardButtonBoxPayment">
          <BlueButton
            className="add-card-btn-light"
            isDonationMode={isDonationMode}
            onClick={() => setModalShow(true)}
            label={
              <span className="add-card-btn-label add-card-btn-single-line">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                  <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Add Card
              </span>
            }
          />
        </Grid>
      )}
    </Grid>
  );
};

export default CardPayment;
